/**
 * Authentication API.
 *
 * Login uses the shared `/api/v1/auth/login` endpoint (same JWT issuer as
 * metsadata internal admin UI). Registration goes through the SaaS-specific
 * `/api/v1/smt/auth/register` flow because new users start in status=pending
 * and require admin approval before they can log in.
 */
import { request, requestData } from './client.ts';
import type { LoginResponse, User } from '../types/api.ts';
import { setSession, type Session } from '../state/session.ts';

export interface LoginInput {
  email: string;
  password: string;
}

export async function login(input: LoginInput): Promise<Session> {
  const data = await requestData<LoginResponse>('/auth/login', {
    method: 'POST',
    body: input,
    skipAuthRedirect: true,
  });
  const session: Session = {
    token: data.token,
    expiresAt: data.expires_at,
    user: data.user as User,
  };
  setSession(session);
  return session;
}

export interface RegisterInput {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  organization?: string;
  address?: string;
  desired_level?: number;
  gdpr_consent?: string;
}

export async function register(input: RegisterInput): Promise<void> {
  await request<unknown>('/smt/auth/register', {
    method: 'POST',
    body: input,
    skipAuthRedirect: true,
  });
}

export async function requestPasswordReset(email: string): Promise<void> {
  await request<unknown>('/smt/auth/password-reset/request', {
    method: 'POST',
    body: { email },
    skipAuthRedirect: true,
  });
}

export async function confirmPasswordReset(token: string, password: string): Promise<void> {
  await request<unknown>('/smt/auth/password-reset/confirm', {
    method: 'POST',
    body: { token, password },
    skipAuthRedirect: true,
  });
}
